import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import axios from "axios";

import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { Toast } from 'primereact/toast';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';

const GrupoGasto = () => {
    const toast = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { administracionId } = useAuth();
    const [loadingBackdrop, setLoadingBackdrop] = useState(false);

    // ─── Helpers ────────────────────────────────────────────────────────────────

    const camelToHeader = (key) =>
        key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();

    const formatValue = (value, type) => {
        if (value === null || value === undefined) return "";
        switch (type) {
            case "date":
                return new Date(value).toLocaleDateString("es-AR");
            case "currency":
                return typeof value === "number"
                    ? value.toLocaleString("es-AR", { style: "currency", currency: "ARS" })
                    : value;
            default:
                return typeof value === "object" ? JSON.stringify(value) : value;
        }
    };

    const flattenRow = (row) => {
        const flat = {};
        for (const [key, value] of Object.entries(row)) {
            if (typeof value === "object" && value !== null && !key.endsWith("_type")) {
                for (const [nestedKey, nestedValue] of Object.entries(value)) {
                    flat[nestedKey] = nestedValue;
                    flat[`${nestedKey}_type`] = row[`${key}_type`] ?? "currency";
                }
            } else {
                flat[key] = value;
            }
        }
        return flat;
    };

    // ─── Estado ─────────────────────────────────────────────────────────────────

    const initialFormData = {
        grupoGastoId: 0,
        descripcion: "",
        orden: "",
        activo: true,
    };

    const [formData, setFormData] = useState(initialFormData);
    const [visible, setVisible] = useState(false);
    const [gruposGasto, setGruposGasto] = useState([]);

    const handleChange = (campo, valor) => {
        setFormData((prev) => ({ ...prev, [campo]: valor }));
    };

    // ─── Submit ─────────────────────────────────────────────────────────────────

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${HOST_API}api/grupogastos/SaveGrupoGasto`, { ...formData, administracionId });
            setVisible(false);
            fetchGruposGasto();
        } catch (err) {
            console.error(err);
        }
    };

    // ─── Fetch ──────────────────────────────────────────────────────────────────

    const fetchGruposGasto = useCallback(async () => {
        setLoadingBackdrop(true);
        try {
            const response = await axios.get(`${HOST_API}api/grupogastos/GetGrupoGastos?administracionId=${administracionId}`);
            console.log(response.data);
            setGruposGasto(response.data);
        } catch (err) {
            console.error(err.message || "Error al obtener grupos de gasto");
        } finally {
            setLoadingBackdrop(false);
        }
    }, [administracionId]);

    useEffect(() => {
        fetchGruposGasto();
    }, [fetchGruposGasto]);

    const gruposGastoFlat = useMemo(() => gruposGasto?.map(flattenRow) ?? [], [gruposGasto]);

    const columns = useMemo(() => {
        if (!gruposGastoFlat?.length) return [];
        const firstRow = gruposGastoFlat[0];
        return Object.keys(firstRow)
            .filter((key) => !key.endsWith("_type") && key !== 'administracionId' && key !== 'activo')
            .map((field) => ({
                field,
                type: firstRow[`${field}_type`] ?? "text",
                header: camelToHeader(field),
            }));
    }, [gruposGasto]);

    // ─── Edit / Delete ──────────────────────────────────────────────────────────

    const handleEdit = (row) => {
        setFormData({
            grupoGastoId: row.grupoGastoId,
            descripcion:  row.descripcion  || '',
            orden:        row.orden        || '',
            activo:       row.activo       ?? true,
        });
        setVisible(true);
    };

    const handleDelete = (id) => {
        confirmDialog({
            message:          '¿Estás seguro de eliminar este grupo de gasto?',
            header:           'Confirmar eliminación',
            icon:             'pi pi-exclamation-triangle',
            acceptLabel:      'Eliminar',
            rejectLabel:      'Cancelar',
            acceptClassName:  'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/grupogastos/DeleteGrupoGasto?grupoGastoId=${id}&administracionId=${administracionId}`);
                    fetchGruposGasto();
                } catch (err) {
                    console.error(err);
                }
            },
        });
    };

    // ─── Render ─────────────────────────────────────────────────────────────────

    return (
        <>
            <h4>Grupo Gasto</h4>
            <br />

            <ConfirmDialog />
            <Toast ref={toast} />
            <LoadingOverlay visible={loadingBackdrop} />

            <div className="flex align-items-center gap-3 mb-3">
                <Button
                    label="Nuevo Grupo Gasto"
                    icon="pi pi-plus"
                    onClick={() => { setFormData(initialFormData); setVisible(true); }}
                />
            </div>

            <Dialog
                header="Grupo Gasto"
                visible={visible}
                style={{ width: "50vw" }}
                breakpoints={{ "960px": "100vw" }}
                modal
                onHide={() => setVisible(false)}
            >
                <form className="p-fluid grid" onSubmit={handleSubmit}>
                    <div className="col-12">
                        <div className="col-12 flex flex-wrap gap-3 mt-3">

                            <div className="w-full">
                                <label>Descripción</label>
                                <InputText
                                    type="text"
                                    value={formData.descripcion || ""}
                                    onChange={(e) => handleChange("descripcion", e.target.value)}
                                    className="p-inputtext-sm w-full"
                                />
                            </div>

                            <div className="w-full md:w-4">
                                <label>Orden</label>
                                <InputText
                                    type="number"
                                    value={formData.orden || ""}
                                    onChange={(e) => handleChange("orden", e.target.value)}
                                    className="p-inputtext-sm w-full"
                                />
                            </div>

                            <div className="w-full md:w-4 flex align-items-center gap-2 mt-2">
                                <Checkbox
                                    inputId="activo"
                                    checked={formData.activo}
                                    onChange={(e) => handleChange("activo", e.checked)}
                                />
                                <label htmlFor="activo">Activo</label>
                            </div>

                            <div className="col-12 mt-3">
                                <Button label="Guardar" icon="pi pi-save" type="submit" />
                            </div>

                        </div>
                    </div>
                </form>
            </Dialog>

            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={gruposGastoFlat}
                        paginator
                        rows={10}
                        showGridlines
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        responsiveLayout="scroll"
                        emptyMessage="No hay grupos de gasto cargados"
                        className="p-datatable-gridlines bg-white"
                    >
                        {columns.map(({ field, header, type }) => (
                            <Column
                                key={field}
                                field={field}
                                header={header}
                                sortable
                                body={(row) => formatValue(row[field], type)}
                            />
                        ))}
                        <Column
                            header="Estado"
                            style={{ width: '8rem' }}
                            body={(row) => {
                                const s = row.activo
                                    ? { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5', label: 'Activo' }
                                    : { bg: '#fef3f2', color: '#f04438', border: '#fecdca', label: 'No Activo' };
                                return (
                                    <span style={{
                                        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                                        fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
                                    }}>
                                        {s.label}
                                    </span>
                                );
                            }}
                        />
                        <Column
                            header="Acciones"
                            style={{ width: '8rem' }}
                            body={(row) => (
                                <div className="flex gap-2 justify-content-center">
                                    <Button icon="pi pi-pencil" rounded text severity="warning" onClick={() => handleEdit(row)} tooltip="Editar" />
                                    <Button icon="pi pi-trash" rounded className="btn-delete" onClick={() => handleDelete(row.grupoGastoId)} tooltip="Eliminar" />
                                </div>
                            )}
                        />
                    </DataTable>
                </div>
            </div>
        </>
    );
};

export default GrupoGasto;
